import React, { useEffect, useState, useContext } from 'react';
import { useFormContext } from 'react-hook-form';
import { StepsBannerBullets } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NewClaimPopup } from '../../components/claims/newClaim/newClaimPopup/newClaimPopup';
import { FaqsBlock } from '../../components/helpCentre/faqsBlock/faqsBlock';
import { NewClaimDetails } from '../../components/claims/newClaim/newClaimDetails/newClaimDetails';
import { NewClaimResult } from '../../components/claims/newClaim/newClaimResult/newClaimResult';
import '../../components/claims/newClaim/newClaim.scss';
import { useGetPetInfoQuery } from '../../data/policy';
import { usePostClaimVetDetailsMutation, usePostClaimVetEndorsementsMutation } from '../../data/vets';
import {
  useGetSavedClaimQuery,
  usePostClaimAttachmentMutation,
  usePostSaveClaimMutation,
  usePostSubmitClaimMutation
} from '../../data/claim';
import { FORM_KEYS } from '../../constants/formKeys';
import {
  EVENT_KEY,
  PETSURE_UNDERWRITER,
  STEP_1_ENTER_DETAILS,
  STEP_2_REVIEW_SUBMIT,
  STEP_3_CONFIRMATION
} from '../../constants/constants';
import { SESSION_STORAGE_KEYS } from '../../constants/sessionStorageKeys';
import { clearNewlyAddedVets } from '../../store/slice/vetDetailsSlice';
import { SLICES } from '../../constants/slices';
import { useGetFaqContentQuery, useGetMarketingTilesQuery } from '../../data/kontentAi';
import Loading from '../../components/shared/Loading/Loading';
import { MREC_TILES, FAQ_CODENAME } from '../../constants/cmsCodeName';
import { BrandContext } from '../../contextProviders/brandContext';
import { FeatureFlagContext } from '../../contextProviders/featureFlagContext';
import { clearCustomerClaimId } from '../../store/slice/claimSlice';
import { clearFullPolicyNumber } from '../../store/slice/petDetailsSlice';
import { scrollToTop } from '../../utils/scrollUtils';

export const NewClaim = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  const { getValues, trigger, reset } = useFormContext();
  const selectedVetDetails = useSelector((state) => state.vetDetails.newlyAddedVets);
  const { hasNewReimbursement, newReimbursementDetails } = useSelector(
    (state) => state?.[SLICES.CLAIMS].reimbursementDetails
  );
  const customerClaimId = useSelector((state) => state?.[SLICES.CLAIMS]?.customerClaimId);
  const fullPolicyNumber = useSelector((state) => state?.[SLICES.PET_DETAILS]?.fullPolicyNumber);
  const contactIdFromSession = sessionStorage.getItem(SESSION_STORAGE_KEYS.CONTACT_ID);
  const [selectedPet, setSelectedPet] = useState();
  const [selectedPetLoading, setSelectedPetLoading] = useState(true);
  const [activeStep, setActiveStep] = useState(STEP_1_ENTER_DETAILS);
  const [isPopupShowing, setPopupIsShowing] = useState(activeStep === STEP_1_ENTER_DETAILS);
  const [claimStatus, setClaimStatus] = useState();
  const [buttonLabel, setButtonLabel] = useState('Next');
  const { activeBrandContext } = useContext(BrandContext);
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isKontentAiPreviewEnabled = cspConfiguration?.IsKontentAIPreviewEnabled || false;
  const isV3PenTestRemediationEndpointsEnabled = cspConfiguration?.IsV3PenTestRemediationEndpointsEnabled;
  const { data: petDetails, isLoading: petDetailsLoading } = useGetPetInfoQuery(auth0AccessToken, {
    skip: !auth0AccessToken
  });
  const { data: savedClaim, isLoading: savedClaimLoading } = useGetSavedClaimQuery(
    {
      customerClaimId,
      auth0Token: auth0AccessToken,
      isV3ApiEnabled: isV3PenTestRemediationEndpointsEnabled
    },
    {
      skip:
        !customerClaimId ||
        isV3PenTestRemediationEndpointsEnabled === undefined ||
        (isV3PenTestRemediationEndpointsEnabled && !auth0AccessToken)
    }
  );
  const [saveClaim, { isLoading: postSaveClaimLoading, isSuccess: postSaveClaimSuccess }] = usePostSaveClaimMutation();
  const [postSubmitClaim, { isLoading: postSubmitClaimLoading, isSuccess: postSubmitClaimSuccess }] =
    usePostSubmitClaimMutation();
  const [postClaimAttachment, { isLoading: postClaimAttachmentLoading, isSuccess: postClaimAttachmentSuccess }] =
    usePostClaimAttachmentMutation();
  const [postClaimVetEndorsements] = usePostClaimVetEndorsementsMutation();
  const [postClaimVetDetails] = usePostClaimVetDetailsMutation();
  const [hasReimbursementDetails, setHasReimbursementDetails] = useState(true);

  const { data: makeAClaimTiles } = useGetMarketingTilesQuery({
    brandPartner: activeBrandContext.brand.key,
    mrecTile: MREC_TILES.CLAIMS,
    previewEnabled: isKontentAiPreviewEnabled
  });

  const { data: faqsData } = useGetFaqContentQuery({
    brandPartner: activeBrandContext.brand.key,
    faqCodeName: FAQ_CODENAME.MAKE_A_CLAIM,
    previewEnabled: isKontentAiPreviewEnabled
  });

  useEffect(() => {
    if (!location.state) {
      reset({
        claimSummary: '',
        invoices: [],
        vetNotes: []
      });
      setSelectedPet(petDetails?.length === 1 ? petDetails[0] : undefined);
      setActiveStep(STEP_1_ENTER_DETAILS);
      setPopupIsShowing(true);
      setButtonLabel('Next');
    }
    return () => {
      dispatch(clearCustomerClaimId());
      dispatch(clearFullPolicyNumber());
    };
  }, [location, reset, dispatch, petDetails]);

  useEffect(() => {
    const { invoices = [], vetNotes = [] } = getValues();
    const isSaveClaim =
      invoices.length > 0 || vetNotes.length > 0
        ? postSaveClaimSuccess && postClaimAttachmentSuccess
        : postSaveClaimSuccess;
    if (isSaveClaim || postSubmitClaimSuccess) {
      dispatch(clearNewlyAddedVets());
      setActiveStep(STEP_3_CONFIRMATION);
      scrollToTop();
      reset();
    }
  }, [postSaveClaimSuccess, postSubmitClaimSuccess, postClaimAttachmentSuccess, dispatch, getValues, reset]);

  useEffect(() => {
    if (petDetails && fullPolicyNumber) {
      const defaultSelected = petDetails?.find((pet) => pet.FullPolicyNumber === fullPolicyNumber);
      setSelectedPet(defaultSelected);
      setSelectedPetLoading(false);
    }
    if (petDetails && savedClaim && customerClaimId) {
      const defaultSelected = petDetails?.find((pet) => pet.FullPolicyNumber === savedClaim.FullPolicyNumber);
      setSelectedPet(defaultSelected);
      setSelectedPetLoading(false);
    }
    if (petDetails && !customerClaimId) {
      if (petDetails.length === 1) {
        setSelectedPet(petDetails[0]);
      }
      setSelectedPetLoading(false);
    }
  }, [savedClaim, petDetails, customerClaimId, fullPolicyNumber]);

  const handlePetSelection = (selection) => {
    setHasReimbursementDetails(true);
    setSelectedPet(selection.petDetail);
  };

  const sendAttachments = async (files, docType, customerClaimId) => {
    try {
      const formData = new FormData();
      const fileArray = Array.from(files);
      fileArray?.forEach((file, index) => formData.append(index, file));
      formData.append('customerClaimId', customerClaimId);
      formData.append('docType', docType);
      await postClaimAttachment(formData);
    } catch (error) {
      // Error handler
    }
  };

  const createSaveClaimPayload = (claimSummary) => {
    return {
      ClaimDescription: claimSummary,
      ClaimPolicyNumber: selectedPet.FullPolicyNumber,
      CustomerClaimId: customerClaimId || 0,
      CustomerId: contactIdFromSession,
      PartnerNumber: selectedPet.PartnerNumber,
      PetName: selectedPet.Name,
      PetSpecies: selectedPet.Species,
      Status: 'InProgress'
    };
  };

  const createClaimPolicyVets = (selectedVet, customerClaimId) => {
    return {
      VetId: 0,
      CustomerClaimId: customerClaimId,
      PracticeName: selectedVet.activeVet.serviceProvider,
      PracticeNumber: null,
      ServiceProviderNumber: selectedVet.activeVet.serviceProviderNumber,
      PracticeAddress: selectedVet.activeVet.AddressLine1,
      Suburb: selectedVet.activeVet.suburb,
      State: selectedVet.activeVet.state,
      PostCode: selectedVet.activeVet.postCode,
      ContactNumber: selectedVet.activeVet.phoneNumber,
      PracticeEmailAddress: selectedVet.activeVet.email,
      IsUpdateRequest: false,
      IsUPMVet: false,
      PetsTreated: selectedVet.petsTreated
    };
  };

  const handleSaveForLater = async () => {
    if (isV3PenTestRemediationEndpointsEnabled && !auth0AccessToken) return;

    const isValid = await trigger([FORM_KEYS.PET_DETAILS_RADIOS], {
      shouldFocus: true
    });
    if (isValid) {
      const { claimSummary, invoices, vetNotes } = getValues();

      const newInvoices = invoices?.filter((file) => !file?.id);
      const newVetNotes = vetNotes?.filter((file) => !file?.id);

      const saveClaimPayload = createSaveClaimPayload(claimSummary);

      const saveClaimResponse = await saveClaim({
        data: saveClaimPayload,
        auth0Token: auth0AccessToken,
        isV3ApiEnabled: isV3PenTestRemediationEndpointsEnabled
      });

      if (selectedVetDetails?.length > 0) {
        selectedVetDetails.forEach((selectedVet) => {
          const claimPolicyVets = createClaimPolicyVets(selectedVet, saveClaimResponse.data);
          postClaimVetDetails(claimPolicyVets);
        });
      }

      if (saveClaimResponse) {
        setClaimStatus('saved');
        reset();
      }

      if (newInvoices.length > 0) {
        await sendAttachments(newInvoices, 'ClaimInvoice', saveClaimResponse.data);
      }
      if (newVetNotes.length > 0) {
        await sendAttachments(newVetNotes, 'ConsultationNotes', saveClaimResponse.data);
      }
    }
  };

  const handlePreviousStep = () => {
    setActiveStep(activeStep - STEP_1_ENTER_DETAILS);
    setButtonLabel('Next');
    scrollToTop();
  };

  const handleNextStep = async () => {
    if (isV3PenTestRemediationEndpointsEnabled && !auth0AccessToken) return;

    const isValid = await trigger(
      [FORM_KEYS.PET_DETAILS_RADIOS, FORM_KEYS.CLAIM_SUMMARY, FORM_KEYS.INVOICES, FORM_KEYS.VET_NOTES],
      {
        shouldFocus: true
      }
    );
    if (isValid) {
      if (
        selectedPet.Underwriter === PETSURE_UNDERWRITER &&
        !selectedPet?.PolicyDetails.ReimbursementInfo.accountHolderName &&
        !selectedPet?.PolicyDetails.ReimbursementInfo.accountNumber &&
        !hasNewReimbursement &&
        !newReimbursementDetails?.newAccountNumber &&
        !newReimbursementDetails?.newAccountHolderName
      ) {
        setHasReimbursementDetails(false);
        return;
      }

      setHasReimbursementDetails(true);

      if (activeStep === STEP_1_ENTER_DETAILS) {
        setActiveStep(STEP_2_REVIEW_SUBMIT);
        setButtonLabel('Submit');
        scrollToTop();
      }
      if (activeStep === STEP_2_REVIEW_SUBMIT) {
        const { claimSummary, invoices, vetNotes } = getValues();

        const newInvoices = invoices.filter((file) => !file?.id);
        const newVetNotes = vetNotes.filter((file) => !file?.id);

        const saveClaimPayload = createSaveClaimPayload(claimSummary);

        const saveClaimResponse = await saveClaim({
          data: saveClaimPayload,
          auth0Token: auth0AccessToken,
          isV3ApiEnabled: isV3PenTestRemediationEndpointsEnabled
        });

        if (saveClaimResponse) {
          setClaimStatus('success');
          reset();
        }

        const attachmentPromises = [];

        if (newInvoices.length > 0) {
          attachmentPromises.push(sendAttachments(newInvoices, 'ClaimInvoice', saveClaimResponse.data));
        }

        // Wait for all invoices attachment uploads to finish before proceeding
        await Promise.all(attachmentPromises);

        if (newVetNotes.length > 0) {
          attachmentPromises.push(sendAttachments(newVetNotes, 'ConsultationNotes', saveClaimResponse.data));
        }

        // Wait for all vetNotes attachment uploads to finish before proceeding
        await Promise.all(attachmentPromises);

        const submitClaim = {
          CustomerClaimId: saveClaimResponse.data,
          BrandPartner: selectedPet.PartnerNumber,
          PetName: selectedPet.Name,
          PetDOB: selectedPet.DateOfBirth,
          PetGender: selectedPet.PolicyDetails.PetGender,
          PetSpecies: selectedPet.Species,
          PetBreed: selectedPet.Breed,
          PolicyNumber: selectedPet.PolicyNumber,
          FullPolicyNumber: selectedPet.FullPolicyNumber,
          PolicyExpiryDate: null,
          Vets: {},
          CustomerId: contactIdFromSession,
          ClaimDescription: claimSummary
        };

        const claimVetDetails = [
          {
            CustomerClaimId: saveClaimResponse.data,
            PolicyId: selectedPet.PolicyNumber,
            PetName: selectedPet.Name
          }
        ];
        if (selectedVetDetails?.length > 0) {
          selectedVetDetails.forEach(async (selectedVet) => {
            const claimPolicyVets = createClaimPolicyVets(selectedVet, saveClaimResponse.data);
            await postClaimVetDetails(claimPolicyVets);
            await postClaimVetEndorsements(claimVetDetails);
          });
        }

        await postSubmitClaim(submitClaim);
      }
    }
  };
  const showLoading =
    petDetailsLoading ||
    savedClaimLoading ||
    selectedPetLoading ||
    postSaveClaimLoading ||
    postSubmitClaimLoading ||
    postClaimAttachmentLoading;

  const renderStepContent = () => {
    if (showLoading) {
      return (
        <div className="loaderContainer">
          <Loading />
        </div>
      );
    }

    if (activeStep < STEP_3_CONFIRMATION) {
      return (
        <NewClaimDetails
          petDetails={petDetails || []}
          activePet={selectedPet}
          isReadOnly={activeStep === STEP_2_REVIEW_SUBMIT}
          onPetSelect={handlePetSelection}
          onNextStep={handleNextStep}
          onPreviousStep={handlePreviousStep}
          onSaveForLater={handleSaveForLater}
          savedClaim={savedClaim}
          buttonLabel={buttonLabel}
          hasReimbursementDetails={hasReimbursementDetails}
        />
      );
    }

    return (
      <NewClaimResult
        activePet={selectedPet}
        status={claimStatus}
      />
    );
  };

  /*
  TODO: This should be moved to UI Library for modals
  */
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === EVENT_KEY.ESCAPE) {
        setPopupIsShowing(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  return (
    <div
      key={location.key}
      className={`petSureForm verticalStacked makeAClaim ${activeStep === STEP_2_REVIEW_SUBMIT ? 'readonly' : ''}`}
    >
      <header className="makeAClaimHeader">
        {isPopupShowing && (
          <NewClaimPopup
            isOpen
            onClose={() => setPopupIsShowing(false)}
          />
        )}

        <h1 className="mainPageHeader">Make a claim</h1>
      </header>

      <StepsBannerBullets
        steps={[
          {
            label: 'Enter details',
            isActive: activeStep === STEP_1_ENTER_DETAILS
          },
          {
            label: 'Review & submit',
            isActive: activeStep === STEP_2_REVIEW_SUBMIT
          },
          {
            label: 'Confirmation',
            isActive: activeStep === STEP_3_CONFIRMATION
          }
        ]}
        customCssClass="stepsBanner"
      />
      <div className="stepContentContainer">{renderStepContent()}</div>
      <aside
        className="faq-tile-container"
        aria-label="Frequently asked questions section"
      >
        {faqsData && faqsData?.FAQs?.questions && (
          <FaqsBlock
            questionSet={faqsData?.FAQs?.questions}
            heading={{ text: 'FAQs' }}
          />
        )}
        {makeAClaimTiles?.data &&
          makeAClaimTiles?.data?.map((item) => {
            return (
              <div
                className={showLoading ? 'marketingTileLoading' : 'marketingTile'}
                key={item.heading}
              >
                {item?.linkUrl ? (
                  <a href={item.linkUrl}>
                    <img
                      src={item.image?.url || item.blobImage}
                      alt={item.image?.alt || item.heading}
                    />
                  </a>
                ) : (
                  <img
                    src={item.image?.url || item.blobImage}
                    alt={item.image?.alt || item.heading}
                  />
                )}
              </div>
            );
          })}
      </aside>
    </div>
  );
};
